import React from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Box, Grid, Typography } from "@mui/material";
import { useTheme } from "@emotion/react";
import PerfectScrollbar from "react-perfect-scrollbar";
import { chartsColorsTheme, containerRef } from "../utilities";

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

const PieChart = ({
  graphData,
  width = "330px",
  height = "calc(100dvh - 25rem)",
  chartMaxWidth = 330,
}) => {
  const theme = useTheme();
  const totalPercentage = graphData?.reduce((total, item) => {
    return total + Number(item?.percentage || 0);
  }, 0);

  // const colorsTheme = [
  //   "#4285F4", // Blue
  //   "#AA46BB", // Purple
  //   "#0F9D58", // Green
  //   "#DB4437", // Red
  //   "#FF6F00", // Orange
  //   "#A5733C", // Brown
  //   "#FFCC00", // Yellow
  //   "#FF00FF", // Pink
  //   "#00C3E3", // Cyan
  // ];
  // const colorsTheme = [
  //   "#68d4cd",
  //   "#cff67b",
  //   "#94dafb",
  //   "#fd8080",
  //   "#6d848e",
  //   "#26a0fc",
  //   "#26e7a6",
  //   "#fbb938",
  //   "#fab1b2",
  //   "#8b75d7",
  // ]

  // const colorsTheme = [
  //   "rgba(66, 133, 244, 0.6)",  // Blue
  //   "rgba(170, 70, 187, 0.6)",  // Purple
  //   "rgba(15, 157, 88, 0.6)",   // Green
  //   "rgba(219, 68, 55, 0.6)",   // Red
  //   "rgba(255, 111, 0, 0.6)",   // Orange
  //   "rgba(165, 115, 60, 0.6)",  // Brown
  //   "rgba(255, 204, 0, 0.6)",   // Yellow
  //   "rgba(255, 0, 255, 0.6)",   // Pink
  //   "rgba(0, 195, 227, 0.6)"    // Cyan
  // ];

  // const colorsTheme = [
  //   "rgba(66, 133, 244, 0.9)",  // Blue
  //   "rgba(170, 70, 187, 0.9)",  // Purple
  //   "rgba(15, 157, 88, 0.9)",   // Green
  //   "rgba(219, 68, 55, 0.9)",   // Red
  //   "rgba(255, 111, 0, 0.9)",   // Orange
  //   "rgba(165, 115, 60, 0.9)",  // Brown
  //   "rgba(255, 204, 0, 0.9)",   // Yellow
  //   "rgba(255, 0, 255, 0.9)",   // Pink
  //   "rgba(0, 195, 227, 0.9)"    // Cyan
  // ];

  const colorsTheme = [...chartsColorsTheme];

  const remainingPercentage = 100 - totalPercentage;

  const data = {
    labels: [],
    datasets: [
      {
        data: graphData
          ? [
            ...graphData?.map((item) => Number(item?.percentage)),
            remainingPercentage > 0 && remainingPercentage < 100
              ? remainingPercentage
              : null,
          ].filter(Boolean) // filter out null values
          : [],
        backgroundColor: [...colorsTheme],
        borderColor: ["#ffffff"],
        borderWidth: graphData?.length === 1 && totalPercentage === 100 ? 0 : 1.13,
      },
    ],
  };

  const options = {
    cutout: "40%", // Adjust the size of the center hole
    responsive: true,
    animation: false,
    width: 330,
    plugins: {
      legend: {
        position: "top",
      },
      tooltip: false,
      datalabels: {
        color: "#fff",
        formatter: (value) => value != null && value != 0 && value > 5 ? `${value}%` : '',
        font: {
          size: 10,
          weight: "bold",
        },
        align: "center",
        anchor: "center",
      },
    },
  };

  return (
    <div
      style={{
        width: width,
        height: height,
        marginInline: "auto",
      }}
    >
      {totalPercentage > 0 ? (
        <>
          {/* `style` is an inline style attribute, so the nested "& > canvas" rule
              that used to live here never applied - only `sx` supports selectors.
              Centring the canvas with flex works regardless of the size Chart.js
              gives it. */}
          <Box
            sx={{
              maxWidth: chartMaxWidth,
              marginInline: "auto",
              display: "flex",
              justifyContent: "center",
              "& > canvas": { marginInline: "auto" },
            }}
          >
            <Doughnut data={data} options={options} />
          </Box>
          <PerfectScrollbar
            containerRef={containerRef}
            id="piechart-scrollbar"
            style={{ marginBlock: "1rem", maxHeight: "-webkit-fill-available" }}
          >
            <Grid
              container
              xs={12}
              width={width}
              sx={{
                border: '1px solid rgb(208, 213, 221)',
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              {graphData && (totalPercentage < 100 ? [...graphData, { name: "Others", percentage: 100 - totalPercentage }] : [...graphData])?.map(({ name, percentage }, index) => {
                return (
                  <Grid
                    item
                    xs={12}
                    display={"flex"}
                    justifyContent={"space-between"}
                    sx={{
                      padding: "12px 16px",
                      backgroundColor:
                        index % 2 == 0 ? theme.palette.customColors.inputField : "",
                    }}
                  >
                    <Typography>{name}</Typography>
                    <Typography color={colorsTheme[index]}>
                      {percentage}%
                    </Typography>
                  </Grid>
                );
              })}
            </Grid>
          </PerfectScrollbar>
        </>
      ) : (
        // The empty state used to be `calc(100dvh - 5rem)` tall - almost a full
        // viewport - regardless of the card it sits in, which is why the Fund
        // Allocation card stretched down the page with the message stranded in the
        // middle of it. A modest minHeight is all it needs.
        <Grid
          display={"grid"}
          sx={{
            placeItems: "center",
            textAlign: "center",
            minHeight: { xs: 180, md: 220 },
            padding: "1.5rem 1rem",
            color: (t) => t.palette.customColors?.g200,
            fontSize: "0.9rem",
          }}
        >
          Fill The Fund Allocation to View Graph
        </Grid>
      )}
    </div>
  );
};

export default PieChart;
